/**
 * RWBR — TROUBLE results.
 *
 * RULE (rulebook p.v, verbatim):
 *   "When a 'TROUBLE' result occurs, you'll roll the two metallic dice for each
 *    driver in the given group starting from the leading position.  (NOTE that
 *    for TROUBLE results, the race LEADER is considered to be part of the TOP
 *    group.)  If the die roll is equal to or less than the driver's 'ESCAPE'
 *    rating, he will have avoided the event. ...  If the die roll is greater
 *    than his 'ESCAPE' rating, his car is 'collected' in the event.  In that
 *    case, you'll roll again for that driver to see if his car can be repaired.
 *    Check the TROUBLE section of the REPAIR ROLL chart on Board B ...
 *
 *    If one or more cars are collected in a TROUBLE result, a YELLOW flag will
 *    come out. ...  If one or two cars are involved, there is one YELLOW FLAG
 *    turn.  If more than two cars are involved, there are two YELLOW FLAG
 *    turns."
 *
 * RULE (rulebook p.xiv, PERFORMANCE CHIPS):
 *   "A driver can use a performance chip to avoid trouble. ... Ordinarily, his
 *    car would be 'collected' in the event, but if Kahne has a performance
 *    chip, he can spend it to avoid the trouble and hold his position."
 *   And: "a driver CANNOT use a performance chip to avoid a PROBLEM." — that
 *   prohibition is enforced in problem.ts, which never consults a chip.
 *
 * RULE (rulebook p.xv, THE BIG ONE, optional):
 *   "If three or more drivers in the TOP group, or six or more drivers in the
 *    MIDDLE group are 'collected' in a single TROUBLE result, continue rolling
 *    for TROUBLE in the NEXT group down."
 *
 * RULE (rulebook p.xvi, TRACK CARD ADJUSTMENTS): the track's ESCAPE adjustment
 *   modifies ALL drivers' ESCAPE ratings; its REPAIR adjustment modifies all
 *   pit crew repair ratings.
 *
 * PURITY: no input is mutated; the returned object describes the changes to
 * apply. The seeded `Dice` is the only stateful argument.
 */

import type { Car, PitCrewGrade, TraitCode } from './types.ts';
import {
  ChartBTracker,
  TROUBLE_TYPE_NAMES,
  bigOneFrom,
  involvement,
  intensityFor,
  matrixFor,
  typeForFace,
  type TroubleType,
  type TrackType,
} from './troubleMatrix.ts';
import { groupOf } from './types.ts';
import type { Dice } from './dice.ts';
import { rollInjury, CITE_D_EZ_16, type InjuryContext, type InjuryResult } from './injuries.ts';
import {
  CITE,
  adjustmentValue,
  resolveRepair,
  type DiceRecord,
  type LedgerEvent,
  type Modifier,
  type PositionDelta,
  type RepairResult,
  type TrackAdjustment,
} from './repair.ts';

// ---------------------------------------------------------------------------
// EZRA driver traits that bite inside a TROUBLE check (EZRA-S2 §4.0)
// ---------------------------------------------------------------------------

/** SS — Super Speedway Expert. */
export const SS_ESCAPE_BONUS = 1;
export const CITE_SS_TRAIT =
  'EZRA-S2 §4.0 — "Super Speedway Experts are assigned a +1 Escape bonus on Super Speedways only ' +
  'and only for Escape checks related to Accidents, Crashes and Wrecks."';

/** AW — Awareness. A 1d6 of 6 buys the car out of trouble it had already lost. */
export const AW_AVOIDS_ON = 6;
export const CITE_AW_TRAIT =
  'EZRA-S2 §4.0 — "Awareness allows the driver who has failed a Trouble Escape roll to roll a 1d6 ' +
  'on a result of 6 his awareness allows the Trouble to be avoided."';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** The three groups TROUBLE can be called on. The LEADER rides with TOP (p.v). */
export type TroubleGroup = 'top' | 'middle' | 'bottom';

export interface TroubleCar {
  driverId: string;
  name?: string;
  /** 1-based running order. 1 = leader. */
  position: number;
  /** 2d6 must be <= this to escape. */
  escape: number;
  pitCrewGrade: PitCrewGrade;
  /** Performance chips in hand. Bottom-group cars cannot hold any (p.xv). */
  chips?: number;
  /**
   * EZRA driver trait. Two of the eight bite here: SS adds +1 ESCAPE on a
   * super-speedway, and AW gives a second chance after a failed ESCAPE check.
   */
  trait?: TraitCode;
  /**
   * POST RACE INTERVIEW, "AUTO ESCAPE ONCE- Racer automatically succeeds first
   * escape roll." (D-EZ-15, single race.) Consumed by the FIRST escape check
   * this car faces; `TroubleCheck.grantUsed` reports the spend so the caller
   * can strike it off.
   */
  autoEscapeOnce?: boolean;
  /**
   * D-EZ-76 — the 2025 green thumbs-up triangle. A FAILED REPAIR does not end
   * his race; it puts him at the back of the BOTTOM group and takes his ability
   * to challenge or defend for the rest of the afternoon. Read by
   * `problem.ts`; every other out-of-race row ignores it.
   */
  dnfExempt?: boolean;
  /**
   * POST RACE INTERVIEW, "INCREASE ESCAPE/ REPAIR/ FAST PIT". The card has no
   * repair number — Board B is indexed by crew grade — so the repair half of
   * that action rides on the roll.
   */
  repairBonus?: number;
  /**
   * POST RACE INTERVIEW — "IGNORE OUT ONCE- Ignore any OUT result (one time)
   * from the Problem or Feud/Fume chart." Read by `problem.ts`, which resolves
   * against this same `TroubleCar`.
   */
  ignoreOutOnce?: boolean;
}

export interface TroubleField {
  top: TroubleCar[];
  middle: TroubleCar[];
  bottom: TroubleCar[];
}

/**
 * Whether a car spends a chip to avoid being collected.
 *
 * AMBIGUITY: p.xiv permits the spend but does not make it automatic; the
 * solitaire simplification on the same page ("a driver who HAS a performance
 * chip automatically SPENDS it") is written about challenges and defences, not
 * about TROUBLE. Default here is 'auto' — the chip is worth less than the car —
 * but the policy is a parameter so a head-to-head game can decide case by case
 * (p.xvi: the decision "must be made BEFORE any dice are rolled").
 */
export type ChipPolicy = 'auto' | 'never' | ((car: TroubleCar) => boolean);

export interface TroubleMatrixContext {
  /** The race's Chart B index card. Lives for the whole race, not the turn. */
  tracker: ChartBTracker;
  track: TrackType;
  /** The track card's own adjustments, as numbers — they move Chart A. */
  escapeAdjustment: number;
  repairAdjustment: number;
  /**
   * Where the involvement chain starts, per EZRA: "Use the Driver Finder for the
   * appropriate group to start the involvement chain." Passed in as a callback
   * because the Driver Finder lives in `problem.ts`, which already imports this
   * module — calling it directly would close an import cycle. Returns a 0-based
   * index into the group, or null to start at the front.
   */
  driverFinder?: (group: TroubleGroup, size: number) => number | null;
}

export interface TroubleInput {
  field: TroubleField;
  /** One group, or several (RARE 33 calls TROUBLE in more than one at once). */
  group: TroubleGroup | TroubleGroup[];
  turn: number;
  totalTurns: number;
  escapeAdj?: TrackAdjustment;
  repairAdj?: TrackAdjustment;
  dice: Dice;
  /** THE BIG ONE cascade (p.xv, optional rule). Default ON. */
  bigOne?: boolean;
  chipPolicy?: ChipPolicy;
  /**
   * RARE "BIG TROUBLE" rows print "roll for every driver twice". Default 1.
   *
   * AMBIGUITY: the row says to roll twice but never says how to read the pair.
   * Implemented as two independent escape checks, collected if EITHER misses —
   * the reading that makes "BIG TROUBLE" bigger than ordinary trouble, which is
   * plainly the intent. Both dice are rolled even when the first already
   * misses, so the printed instruction and the dice log agree. Only ONE repair
   * roll follows, and one chip still buys the whole incident off.
   */
  rollsPerDriver?: number;
  /** The laps-down optional rule is in force — a repaired car loses a lap (D-EZ-12). */
  lapsDown?: boolean;
  /**
   * D-EZ-16 — take the EZRA injury sheet's trigger LITERALLY ("fails their
   * Escape roll"), so a driver checked for injury even when a performance chip
   * or the AW trait took him back out of the incident. Default OFF; see the
   * ruling in `injuries.ts` and the measured gap in `scripts/injuries.ts`.
   */
  injuryOnFailedRoll?: boolean;
  /**
   * EZRA Trouble Matrix (D-015). Absent = the base game's TROUBLE, unchanged.
   *
   * Present means this incident gets a TYPE off Chart A, is rationed by the
   * Chart B tracker the race is carrying, and takes Chart C's per-type ESCAPE
   * and REPAIR deltas on top of the track card's own.
   *
   * EZRA scopes all of it: "The Optional Trouble Adjustments are restricted to
   * Group Trouble events. They do not apply to individual driver trouble (Feuds,
   * Driver Incidents, etc.)" — so a lost duel still runs base-game trouble, and
   * the caller simply does not pass this.
   */
  matrix?: TroubleMatrixContext;
  /**
   * Is this a super-speedway? Only read by the SS trait, which EZRA scopes to
   * "Super Speedways only and only for Escape checks related to Accidents,
   * Crashes and Wrecks" — which is exactly what a TROUBLE escape check is.
   * Absent is treated as "no", so a caller who never heard of traits is safe.
   */
  superSpeedway?: boolean;
  /** Narration: what caused this TROUBLE (a race-cam call, a duel, a feud). */
  cause?: string;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface TroubleCheck extends LedgerEvent {
  eventType: 'trouble';
  momentTag: 'trouble.escaped' | 'trouble.chip-saved' | 'trouble.collected';
  driverId: string;
  group: TroubleGroup;
  position: number;
  /** The ESCAPE rating printed on the card. */
  baseEscape: number;
  /** After the track card's ESCAPE adjustment (p.xvi). */
  escape: number;
  /** Every 2d6 sum rolled for this car, in order. */
  rolls: number[];
  escaped: boolean;
  collected: boolean;
  chipSpent: boolean;
  /** Only present when the car was collected and had to try to repair. */
  repair: RepairResult | null;
  /**
   * Set when a one-race interview grant was spent on this check, so the caller
   * can strike it off the car. A grant that fires twice is not a grant.
   */
  grantUsed?: 'auto-escape-once';
  /**
   * EZRA INJURY TABLES — rolled when this driver FAILED his escape roll on a
   * CRASH or a WRECK. Null when no injury check applied or none resulted.
   */
  injury: InjuryResult | null;
}

export type TroubleMomentTag =
  | 'trouble.no-contact'
  | 'trouble.caution'
  | 'trouble.multi-car'
  | 'wreck.big-one';

export interface TroubleResult extends LedgerEvent {
  eventType: 'trouble';
  momentTag: TroubleMomentTag;
  groupsRolled: TroubleGroup[];
  checks: TroubleCheck[];
  escapedIds: string[];
  chipSavedIds: string[];
  collectedIds: string[];
  repairedIds: string[];
  outIds: string[];
  /** 0, 1 or 2 (p.v). */
  yellowTurns: 0 | 1 | 2;
  yellowFlag: boolean;
  bigOneTriggered: boolean;
  /** EZRA Chart A's verdict, or null when the base game's TROUBLE ran. */
  troubleType: TroubleType | null;
  chipsSpent: number;
  chipsForfeited: number;
  /** Every injury this incident produced, in check order. Usually empty. */
  injuries: InjuryResult[];
  /** Drivers who spent a one-race interview grant here, so it can be struck off. */
  grantsUsed: { driverId: string; grant: 'auto-escape-once' }[];
  /** Serious injuries — the driver "cannot contiue racing today". */
  injuredOutIds: string[];
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/** p.xv — cascade thresholds. BOTTOM has no group below it, so it never cascades. */
export const BIG_ONE_THRESHOLD: Record<TroubleGroup, number | null> = {
  top: 3,
  middle: 6,
  bottom: null,
};

export function nextGroupDown(group: TroubleGroup): TroubleGroup | null {
  if (group === 'top') return 'middle';
  if (group === 'middle') return 'bottom';
  return null;
}

/**
 * p.v — "If one or two cars are involved, there is one YELLOW FLAG turn.  If
 * more than two cars are involved, there are two YELLOW FLAG turns."
 * Zero collected = no flag at all ("If one or more cars are collected ... a
 * YELLOW flag will come out").
 *
 * AMBIGUITY: "involved" is read here as COLLECTED. A driver who spends a chip
 * "avoid[s] the trouble and hold[s] his position" (p.xiv) and so is not
 * collected, meaning a single chip can suppress the caution entirely when he
 * was the only car in it — and, for the same reason, can hold a wreck under THE
 * BIG ONE's three-car threshold. The alternative reading (a chip saves the car
 * but the incident still happened) is defensible; it is not what the chip rule
 * says, so it is not what is implemented.
 */
export function yellowTurnsForCollected(collected: number): 0 | 1 | 2 {
  if (collected <= 0) return 0;
  if (collected <= 2) return 1;
  return 2;
}

/** p.xvi — the track's ESCAPE adjustment applies to every driver's rating. */
export function adjustedEscape(baseEscape: number, escapeAdj: TrackAdjustment): number {
  return baseEscape + adjustmentValue(escapeAdj);
}

/**
 * Build the three TROUBLE groups from a running order.
 * The LEADER is placed at the head of TOP (p.v). Retired cars are skipped.
 * Cars come out sorted by position, i.e. "starting from the leading position".
 */
export function troubleFieldFromGrid(grid: Car[]): TroubleField {
  const field: TroubleField = { top: [], middle: [], bottom: [] };
  const running = grid.filter((c) => c.status === 'running');
  for (const car of running) {
    const g = groupOf(car.position);
    const entry: TroubleCar = {
      driverId: car.card.driverId,
      name: car.card.name,
      position: car.position,
      escape: car.card.escape,
      pitCrewGrade: car.card.pitCrewGrade,
      chips: car.chips,
      ...(car.card.trait ? { trait: car.card.trait } : {}),
      ...(car.grants?.autoEscapeOnce ? { autoEscapeOnce: true } : {}),
      ...(car.grants?.ignoreOutOnce ? { ignoreOutOnce: true } : {}),
      ...(car.grants?.repairBonus ? { repairBonus: car.grants.repairBonus } : {}),
      // D-EZ-76 — printed on the card, not granted by an interview, so it comes
      // off `card` rather than `grants` and it never runs out.
      ...(car.card.dnfExempt ? { dnfExempt: true } : {}),
    };
    if (g === 'leader' || g === 'top') field.top.push(entry);
    else if (g === 'middle') field.middle.push(entry);
    else field.bottom.push(entry);
  }
  field.top.sort((a, b) => a.position - b.position);
  field.middle.sort((a, b) => a.position - b.position);
  field.bottom.sort((a, b) => a.position - b.position);
  return field;
}

function spendableChips(car: TroubleCar): number {
  // p.xv — performance chips cannot be carried by drivers in the BOTTOM group.
  if (groupOf(car.position) === 'bottom') return 0;
  return Math.max(0, car.chips ?? 0);
}

/** Chart C's ESCAPE delta for this incident's type, or 0 when the matrix is off. */
function chartCEscape(input: TroubleInput, type: TroubleType | null): number {
  if (!input.matrix || !type) return 0;
  return intensityFor(input.matrix.track, type).escape;
}

/** Chart C's REPAIR delta. Folded into the repair call, not the escape check. */
function chartCRepair(input: TroubleInput, type: TroubleType | null): number {
  if (!input.matrix || !type) return 0;
  return intensityFor(input.matrix.track, type).repair;
}

function wantsChip(car: TroubleCar, policy: ChipPolicy): boolean {
  if (spendableChips(car) < 1) return false;
  if (policy === 'never') return false;
  if (policy === 'auto') return true;
  return policy(car);
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * EZRA's injury sheet triggers on "a Crash or a Wreck" by name. An ACCIDENT is
 * not on the list and neither is base-game TROUBLE, which has no type at all —
 * so with the Trouble Matrix off, nobody is ever hurt. That is the printed
 * scope, not an oversight on our side.
 */
function injuryContextFor(type: TroubleType | null): InjuryContext | null {
  if (type === 'CR') return 'CRASH';
  if (type === 'WK') return 'WRECK';
  return null;
}

function checkOneCar(
  car: TroubleCar,
  group: TroubleGroup,
  input: TroubleInput,
  rollsPerDriver: number,
  policy: ChipPolicy,
  type: TroubleType | null = null,
): TroubleCheck {
  // POST RACE INTERVIEW — "AUTO ESCAPE ONCE- Racer automatically succeeds first
  // escape roll." Resolved BEFORE any die is thrown, because the sheet says the
  // roll succeeds, not that it is re-rolled. No dice are recorded for it, which
  // is the honest log: none were thrown.
  if (car.autoEscapeOnce) {
    const who0 = car.name ?? car.driverId;
    return {
      eventType: 'trouble',
      momentTag: 'trouble.escaped',
      ruleCitation: `${CITE.trouble}; POST RACE INTERVIEW — "AUTO ESCAPE ONCE"`,
      actorIds: [car.driverId],
      dice: [],
      modifiers: [
        { name: 'ESCAPE rating', value: car.escape, detail: `printed on ${who0}'s card` },
        { name: 'group', value: group },
        {
          name: 'interview grant — AUTO ESCAPE ONCE',
          value: 'spent',
          detail: 'RWBR_postraceinterview.pdf — "Racer automatically succeeds first escape roll."',
        },
      ],
      description: `${who0} is straight through it — his first escape of the day was never in doubt.`,
      positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
      driverId: car.driverId,
      group,
      position: car.position,
      baseEscape: car.escape,
      escape: car.escape,
      rolls: [],
      escaped: true,
      collected: false,
      chipSpent: false,
      repair: null,
      grantUsed: 'auto-escape-once',
      injury: null,
    };
  }

  // EZRA INJURY TABLES. Null for an ACCIDENT and for base-game TROUBLE, because
  // the sheet names "a Crash or a Wreck" and nothing else.
  const injuryCtx = injuryContextFor(type);
  /**
   * D-EZ-16's literal reading, off by default.
   *
   * The sheet triggers on the failed roll; the chip (RB/p.xiv) and AW (EZRA-S2
   * §4.0) both print the word "avoid". Default: a driver who avoided the
   * incident is not checked at all. Turn `injuryOnFailedRoll` on and he is —
   * and `scripts/injuries.ts` measures both, so the gap between the two
   * readings is a number rather than an opinion.
   */
  const litInjury = (via: 'AW' | 'chip'): InjuryResult | null => {
    if (!injuryCtx || input.injuryOnFailedRoll !== true) return null;
    const hurt = rollInjury({
      driverId: car.driverId,
      name: car.name,
      context: injuryCtx,
      dice: input.dice,
      turn: input.turn,
    });
    hurt.modifiers.push({ name: 'D-EZ-16 literal reading', value: via, detail: CITE_D_EZ_16 });
    return hurt;
  };

  const escapeAdjValue = adjustmentValue(input.escapeAdj) + chartCEscape(input, type);
  // EZRA SS — "+1 Escape bonus on Super Speedways only and only for Escape
  // checks related to Accidents, Crashes and Wrecks." A TROUBLE escape check is
  // precisely that; nothing else in the engine gets this bonus.
  const ssBonus = car.trait === 'SS' && input.superSpeedway === true ? SS_ESCAPE_BONUS : 0;
  const escape = car.escape + escapeAdjValue + ssBonus;
  const who = car.name ?? car.driverId;

  const modifiers: Modifier[] = [
    { name: 'ESCAPE rating', value: car.escape, detail: `printed on ${who}'s card` },
    { name: 'group', value: group, detail: car.position === 1 ? CITE.leaderIsTop : undefined },
  ];
  if (escapeAdjValue !== 0) {
    modifiers.push({ name: 'track card ESCAPE adjustment', value: escapeAdjValue, detail: CITE.trackAdj });
  }
  if (ssBonus !== 0) {
    modifiers.push({ name: 'SS trait — Super Speedway Expert', value: ssBonus, detail: CITE_SS_TRAIT });
  }
  const cEsc = chartCEscape(input, type);
  if (cEsc !== 0 && type) {
    modifiers.push({
      name: `Chart C ESCAPE — ${TROUBLE_TYPE_NAMES[type]}`,
      value: cEsc,
      detail: 'EZRA-S2 Appendix C, Table C',
    });
  }
  if (type) modifiers.push({ name: 'trouble type', value: TROUBLE_TYPE_NAMES[type], detail: 'EZRA-S2 Appendix C, Table A' });
  if (rollsPerDriver !== 1) {
    modifiers.push({ name: 'rolls per driver', value: rollsPerDriver, detail: 'RARE "BIG TROUBLE" — roll for every driver twice' });
  }

  const diceRecords: DiceRecord[] = [];
  const rolls: number[] = [];
  for (let i = 0; i < rollsPerDriver; i++) {
    const r = input.dice.sum2d6();
    rolls.push(r.total);
    diceRecords.push({
      kind: '2d6-sum',
      dice: r.dice,
      total: r.total,
      label: rollsPerDriver === 1 ? 'ESCAPE check' : `ESCAPE check ${i + 1} of ${rollsPerDriver}`,
    });
  }

  // "equal to or less than ... he will have avoided the event"
  const wouldBeCollected = rolls.some((total) => total > escape);
  const worst = Math.max(...rolls);

  if (!wouldBeCollected) {
    return {
      eventType: 'trouble',
      momentTag: 'trouble.escaped',
      ruleCitation: CITE.trouble,
      actorIds: [car.driverId],
      dice: diceRecords,
      modifiers,
      description:
        `${who} needed ${escape} or less to escape and rolled ${rolls.join(', ')} — clear of it, position held.`,
      positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
      driverId: car.driverId,
      group,
      position: car.position,
      baseEscape: car.escape,
      escape,
      rolls,
      escaped: true,
      collected: false,
      chipSpent: false,
      repair: null,
      injury: null,
    };
  }

  // EZRA AW — "the driver who has failed a Trouble Escape roll [may] roll a 1d6;
  // on a result of 6 his awareness allows the Trouble to be avoided."
  //
  // RULING (D-EZ-4): AW is resolved BEFORE the performance chip. EZRA does not
  // say which comes first, but the awareness roll is free and the chip is a
  // spent resource, and no player alive would burn the chip while a free out is
  // still on the table. Ordering it the other way would make AW nearly
  // worthless under the default 'auto' chip policy, since the chip would always
  // fire first and the trait would only ever be seen by a driver who had no
  // chips — which is not a trait, it is a consolation prize.
  if (car.trait === 'AW') {
    const aw = input.dice.d6();
    diceRecords.push({ kind: 'd6', dice: [aw], total: aw, label: 'AWARENESS (AW trait)' });
    modifiers.push({ name: 'AW trait — Awareness', value: `${aw}`, detail: CITE_AW_TRAIT });
    if (aw === AW_AVOIDS_ON) {
      return {
        eventType: 'trouble',
        momentTag: 'trouble.escaped',
        ruleCitation: `${CITE.trouble}; ${CITE_AW_TRAIT}`,
        actorIds: [car.driverId],
        dice: diceRecords,
        modifiers,
        description:
          `${who} rolled ${worst} against an ESCAPE of ${escape} and was in it — but sees it ` +
          `coming, picks his line and drives clear. Awareness, and a ${aw} on the die.`,
        positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
        driverId: car.driverId,
        group,
        position: car.position,
        baseEscape: car.escape,
        escape,
        rolls,
        escaped: true,
        collected: false,
        chipSpent: false,
        repair: null,
        // D-EZ-16 — AW means he "drives clear". Under the literal reading of
        // the injury sheet's trigger ("fails their Escape roll") he is checked
        // anyway; that is what `injuryOnFailedRoll` turns on.
        injury: litInjury('AW'),
      };
    }
  }

  if (wantsChip(car, policy)) {
    modifiers.push({ name: 'performance chip spent', value: 1, detail: CITE.chips });
    return {
      eventType: 'trouble',
      momentTag: 'trouble.chip-saved',
      ruleCitation: `${CITE.trouble}; ${CITE.chips}`,
      actorIds: [car.driverId],
      dice: diceRecords,
      modifiers,
      description:
        `${who} rolled ${worst} against an ESCAPE of ${escape} and would have been collected, ` +
        `but spends a performance chip to avoid the trouble and hold his position.`,
      positionDeltas: [{ driverId: car.driverId, from: car.position, to: car.position, placement: 'hold' }],
      driverId: car.driverId,
      group,
      position: car.position,
      baseEscape: car.escape,
      escape,
      rolls,
      escaped: false,
      collected: false,
      chipSpent: true,
      repair: null,
      // D-EZ-16 — the chip means he "avoid[s] the trouble" (RB/p.xiv).
      injury: litInjury('chip'),
    };
  }

  const repair = resolveRepair({
    car: {
      driverId: car.driverId,
      name: car.name,
      pitCrewGrade: car.pitCrewGrade,
      position: car.position,
      chips: spendableChips(car),
    },
    table: 'trouble',
    turn: input.turn,
    totalTurns: input.totalTurns,
    repairAdj: input.repairAdj,
    // Chart C's per-type delta, plus any one-race INCREASE REPAIR the driver
    // brought out of his post-race interview (D-EZ-15).
    repairBonus: chartCRepair(input, type) + (car.repairBonus ?? 0),
    lapsDown: input.lapsDown,
    dice: input.dice,
    cause: type ? `collected in the ${TROUBLE_TYPE_NAMES[type].toLowerCase()}` : 'collected in the wreck',
  });

  return {
    eventType: 'trouble',
    momentTag: 'trouble.collected',
    ruleCitation: CITE.trouble,
    actorIds: [car.driverId],
    dice: [...diceRecords, ...repair.dice],
    modifiers: [...modifiers, ...repair.modifiers],
    description:
      `${who} rolled ${worst} against an ESCAPE of ${escape} and is collected in the incident. ` +
      repair.description,
    positionDeltas: repair.positionDeltas,
    driverId: car.driverId,
    group,
    position: car.position,
    baseEscape: car.escape,
    escape,
    rolls,
    escaped: false,
    collected: true,
    chipSpent: false,
    repair,
    injury: injuryCtx
      ? rollInjury({
          driverId: car.driverId,
          name: car.name,
          context: injuryCtx,
          dice: input.dice,
          turn: input.turn,
        })
      : null,
  };
}

/**
 * Resolve a TROUBLE result for a whole group (p.v), including the optional
 * BIG ONE cascade (p.xv).
 */
export function resolveTrouble(input: TroubleInput): TroubleResult {
  const requested: TroubleGroup[] = Array.isArray(input.group) ? [...input.group] : [input.group];
  const bigOne = input.bigOne !== false;
  const policy = input.chipPolicy ?? 'auto';
  const rollsPerDriver = Math.max(1, Math.trunc(input.rollsPerDriver ?? 1));

  const checks: TroubleCheck[] = [];
  const groupsRolled: TroubleGroup[] = [];
  const involvementNotes: string[] = [];
  let bigOneTriggered = false;
  let bigOneReason: string | null = null;

  // ---- EZRA CHART A + B: what kind of incident is this? --------------------
  //
  // Rolled ONCE for the whole incident, before any group is checked, because a
  // Big One that cascades into the next group down is the SAME incident — it
  // must not become a Wreck in TOP and an Accident in MIDDLE.
  let type: TroubleType | null = null;
  let typeRoll: number | null = null;
  let substituted = false;
  let trackerReset = false;
  if (input.matrix) {
    const m = matrixFor(input.matrix.track, input.matrix.escapeAdjustment, input.matrix.repairAdjustment);
    typeRoll = input.dice.d6();
    const wanted = typeForFace(m.effective, typeRoll);
    const taken = input.matrix.tracker.take(wanted, input.dice);
    type = taken.type;
    substituted = taken.substituted;
    trackerReset = taken.reset;
  }

  const queue: TroubleGroup[] = [...requested];
  const seen = new Set<TroubleGroup>();
  let wrapRoll: number | null = null;

  while (queue.length > 0) {
    const group = queue.shift() as TroubleGroup;
    if (seen.has(group)) continue;
    seen.add(group);
    groupsRolled.push(group);

    // "starting from the leading position" — ascending running order.
    const all = [...input.field[group]].sort((a, b) => a.position - b.position);

    // ---- EZRA INVOLVEMENT: how many of them are even checked? --------------
    //
    // A WRECK is "GROUP" — every car — which is exactly what the base game
    // already does, so a Wreck is base-game TROUBLE under a new name. Accidents
    // and Crashes are the genuinely new thing: a handful of cars, found by
    // rolling for a starting point and taking the next N from there. That is
    // what stops every incident from being a 12-car pile-up.
    let cars = all;
    let involvedCount: number | 'group' = 'group';
    let involvementFormula: string | null = null;
    if (input.matrix && type && all.length > 0) {
      const inv = involvement(group, type, input.dice);
      involvedCount = inv.cars;
      involvementFormula = inv.formula;
      if (inv.cars !== 'group') {
        const n = Math.max(1, Math.min(all.length, inv.cars));
        const startAt = input.matrix.driverFinder?.(group, all.length) ?? 0;
        const from = Math.max(0, Math.min(all.length - 1, startAt));
        // Wraps within the group: EZRA's own Accident example wraps in the TOP
        // group rather than spilling into the MIDDLE one.
        cars = Array.from({ length: n }, (_, i) => all[(from + i) % all.length]);
      }
    }

    let collectedHere = 0;
    for (const car of cars) {
      // Presentation only: the table shows this driver's card on his own escape dice.
      const check = input.dice.about([car.driverId], () => checkOneCar(car, group, input, rollsPerDriver, policy, type));
      checks.push(check);
      if (check.collected) collectedHere++;
    }
    if (involvementFormula) {
      involvementNotes.push(
        `${group.toUpperCase()}: ${involvementFormula} → ${involvedCount === 'group' ? 'the whole group' : `${cars.length} car${cars.length === 1 ? '' : 's'}`}`,
      );
    }

    // ---- THE BIG ONE ------------------------------------------------------
    if (input.matrix && type) {
      // EZRA replaces the base thresholds AND gives the BOTTOM group a cascade
      // the rulebook does not have — one that wraps back to the front of the
      // field. Base RWBR simply stops at MIDDLE.
      const verdict = bigOneFrom(group, type, collectedHere, input.dice);
      if (verdict.wrapRoll !== null) wrapRoll = verdict.wrapRoll;
      if (bigOne && verdict.triggers && verdict.into && !seen.has(verdict.into)) {
        bigOneTriggered = true;
        bigOneReason = verdict.reason;
        queue.push(verdict.into);
      }
    } else {
      const threshold = BIG_ONE_THRESHOLD[group];
      const below = nextGroupDown(group);
      if (bigOne && threshold !== null && collectedHere >= threshold && below && !seen.has(below)) {
        bigOneTriggered = true;
        queue.push(below);
      }
    }
  }

  const escapedIds = checks.filter((c) => c.escaped).map((c) => c.driverId);
  const chipSavedIds = checks.filter((c) => c.chipSpent).map((c) => c.driverId);
  const collected = checks.filter((c) => c.collected);
  const collectedIds = collected.map((c) => c.driverId);
  const repairedIds = collected.filter((c) => c.repair?.repaired).map((c) => c.driverId);
  const outIds = collected.filter((c) => c.repair && !c.repair.repaired).map((c) => c.driverId);

  const yellowTurns = yellowTurnsForCollected(collectedIds.length);

  // EZRA INJURY TABLES. `injury` is null on the overwhelming majority of checks
  // — only doubles hurt anybody — so this list is normally empty.
  const injuries = checks.map((c) => c.injury).filter((i): i is InjuryResult => i !== null);
  const realInjuries = injuries.filter((i) => i.severity !== 'none');
  const injuredOutIds = realInjuries.filter((i) => i.outOfRace).map((i) => i.driverId);
  const grantsUsed = checks
    .filter((c) => c.grantUsed)
    .map((c) => ({ driverId: c.driverId, grant: c.grantUsed as 'auto-escape-once' }));

  const modifiers: Modifier[] = [
    { name: 'groups rolled', value: groupsRolled.join(' → ') },
    { name: 'cars checked', value: checks.length },
    { name: 'cars collected', value: collectedIds.length },
  ];
  const escapeAdjValue = adjustmentValue(input.escapeAdj);
  if (escapeAdjValue !== 0) {
    modifiers.push({ name: 'track card ESCAPE adjustment', value: escapeAdjValue, detail: CITE.trackAdj });
  }
  const repairAdjValue = adjustmentValue(input.repairAdj);
  if (repairAdjValue !== 0) {
    modifiers.push({ name: 'track card REPAIR adjustment', value: repairAdjValue, detail: CITE.trackAdj });
  }
  if (type) {
    modifiers.push({
      name: 'Chart A trouble type',
      value: `${TROUBLE_TYPE_NAMES[type]} (${type})`,
      detail: `d6 ${typeRoll}${substituted ? ' — the rolled type had no Chart B spaces left' : ''}${trackerReset ? '; every space was spent, the card resets' : ''}`,
    });
    for (const note of involvementNotes) {
      modifiers.push({ name: 'involvement', value: note, detail: 'EZRA-S2 Appendix C' });
    }
    if (wrapRoll !== null) {
      modifiers.push({
        name: 'BOTTOM group Big One wrap',
        value: wrapRoll,
        detail: 'EZRA-S2 §3.5 — "The Big One in the Bottom Group wraps to the Top Group on a 1d6 roll of 6"',
      });
    }
  }
  if (bigOneTriggered) {
    modifiers.push({
      name: 'THE BIG ONE',
      value: bigOneReason ?? 'cascaded to the next group down',
      detail: CITE.bigOne,
    });
  }
  if (chipSavedIds.length > 0) {
    modifiers.push({ name: 'performance chips spent to avoid trouble', value: chipSavedIds.length, detail: CITE.chips });
  }
  // The spend happened on a per-car CHECK, whose modifiers never reach the
  // event stream — only this aggregate event does. Surfacing it here is what
  // puts it on the ledger, and the ledger is the interface.
  for (const spend of grantsUsed) {
    modifiers.push({
      name: 'interview grant — AUTO ESCAPE ONCE',
      value: spend.driverId,
      detail: 'RWBR_postraceinterview.pdf — "Racer automatically succeeds first escape roll."',
    });
  }
  for (const hurt of realInjuries) {
    modifiers.push({
      name: `injury — ${hurt.severity}`,
      value: hurt.location?.locations.join(' + ') ?? hurt.driverId,
      detail: hurt.description,
    });
  }

  const momentTag: TroubleMomentTag = bigOneTriggered
    ? 'wreck.big-one'
    : collectedIds.length === 0
      ? 'trouble.no-contact'
      : collectedIds.length <= 2
        ? 'trouble.caution'
        : 'trouble.multi-car';

  const positionDeltas: PositionDelta[] = collected.flatMap((c) => c.positionDeltas);

  const causePhrase = input.cause ? `${input.cause}: ` : '';
  const noun = type ? TROUBLE_TYPE_NAMES[type].toLowerCase() : 'trouble';
  /*
    ⚠ "AN WRECK". The article was hard-coded to `an`, and every trouble type
    beginning with a consonant read "an wreck in the top group", "an crash in the
    middle group". It had been in the ledger for as long as the ledger has
    existed; it surfaced on 18 Aug 2026 only because these descriptions are now
    the primary source for a NEWSPAPER (D-EZ-54) and a paper that prints "an
    wreck" is a paper nobody believes.
  */
  const article = /^[aeiou]/.test(noun) ? 'an' : 'a';
  const nounPhrase = noun === 'trouble' ? 'trouble' : `${article} ${noun}`;
  const description =
    collectedIds.length === 0
      ? `${causePhrase}${nounPhrase} in the ${groupsRolled.join(' and ')} group — everybody got through it. No caution.`
      : `${causePhrase}${nounPhrase} in the ${groupsRolled.join(' then ')} group. ` +
        `${collectedIds.length} car${collectedIds.length === 1 ? '' : 's'} collected` +
        (bigOneTriggered ? ' — THE BIG ONE, and it swept into the next group down' : '') +
        `. ${repairedIds.length} repaired to the back, ${outIds.length} out of the race. ` +
        `${yellowTurns} yellow-flag turn${yellowTurns === 1 ? '' : 's'}.`;

  return {
    eventType: 'trouble',
    momentTag,
    troubleType: type,
    ruleCitation: bigOneTriggered ? `${CITE.trouble}; ${CITE.bigOne}; ${CITE.yellow}` : `${CITE.trouble}; ${CITE.yellow}`,
    actorIds: checks.map((c) => c.driverId),
    dice: checks.flatMap((c) => c.dice),
    modifiers,
    description,
    positionDeltas,
    groupsRolled,
    checks,
    escapedIds,
    chipSavedIds,
    collectedIds,
    repairedIds,
    outIds,
    yellowTurns,
    yellowFlag: yellowTurns > 0,
    bigOneTriggered,
    chipsSpent: chipSavedIds.length,
    chipsForfeited: collected.reduce((n, c) => n + (c.repair?.chipsForfeited ?? 0), 0),
    injuries,
    grantsUsed,
    injuredOutIds,
  };
}
